import React, { Fragment } from 'react';
import {
	Box,
	Button,
	HStack,
	Link,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Select,
	SimpleGrid,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	useToast,
} from '@chakra-ui/react';
import { FaChevronDown, FaExternalLinkAlt, FaPaperPlane } from 'react-icons/fa';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import {
	hasPermission,
	PERMISSION_KEYS,
} from '../../../utils/permissionsUtils';
import { useDeleteLead, useUpdateLead } from '../../../hooks/CRM/useOutreach';
import {
	EVENT_LABELS,
	LEAD_STATUSES,
	LEAD_STATUS_META,
	LOGGABLE_EVENTS,
	LOG_MENU_DIVIDERS,
	NEXT_SEND,
	fmtDate,
	isDue,
	isOverdue,
	primaryOf,
	timeAgo,
} from './leadMeta';
import {
	ContactsTab,
	DetailsTab,
	EmailTab,
	TimelineTab,
} from './LeadOutreachPanels';

export const useLeadPermissions = () => {
	const { companyId, userPermissions, isCompanyAdmin } =
		useAuthenticatedUserInfo();
	const can = (action) =>
		isCompanyAdmin ||
		hasPermission(userPermissions, PERMISSION_KEYS.LEADS, [action]);
	return {
		companyId,
		canRead: can('read'),
		canCreate: can('create'),
		canUpdate: can('update'),
		canDelete: can('delete'),
	};
};

/** Status select, "Mark … sent" and the Log menu. */
export const leadActionButtons = ({ lead, onStatus, onLog }) => {
	const nextSend = NEXT_SEND[lead.status];
	return [
		<Select
			key='status'
			size='sm'
			w='auto'
			borderRadius='lg'
			value={lead.status}
			onChange={(e) => onStatus(e.target.value)}
			aria-label='Status'>
			{LEAD_STATUSES.map((s) => (
				<option key={s} value={s}>
					{LEAD_STATUS_META[s].label}
				</option>
			))}
		</Select>,
		nextSend && (
			<Button
				key='send'
				size='sm'
				colorScheme='teal'
				leftIcon={<FaPaperPlane />}
				onClick={() => onLog(nextSend)}>
				Mark {EVENT_LABELS[nextSend].toLowerCase()}
			</Button>
		),
		<Menu key='log' placement='bottom-end'>
			<MenuButton
				as={Button}
				size='sm'
				variant='outline'
				colorScheme='purple'
				rightIcon={<FaChevronDown />}>
				Log
			</MenuButton>
			<MenuList fontSize='sm'>
				{LOGGABLE_EVENTS.map((t, i) => (
					<Fragment key={t}>
						{LOG_MENU_DIVIDERS.includes(i) && <MenuDivider />}
						<MenuItem onClick={() => onLog(t)}>{EVENT_LABELS[t]}</MenuItem>
					</Fragment>
				))}
			</MenuList>
		</Menu>,
	].filter(Boolean);
};

/** Returns a status setter that reports failures as a toast. */
export const useSetLeadStatus = (companyId, lead) => {
	const toast = useToast();
	const updateLead = useUpdateLead(companyId);
	return async (status) => {
		try {
			await updateLead.mutateAsync({ id: lead._id, data: { status } });
		} catch (err) {
			toast({ status: 'error', title: err.message, isClosable: true });
		}
	};
};

/** Confirms, deletes the lead, then calls onDeleted. */
export const useRemoveLead = (companyId, onDeleted) => {
	const toast = useToast();
	const deleteLead = useDeleteLead(companyId);
	const remove = async (lead) => {
		if (!window.confirm(`Delete ${lead.name} and its whole history?`)) return;
		try {
			await deleteLead.mutateAsync(lead._id);
			toast({ status: 'success', title: 'Lead deleted' });
			onDeleted();
		} catch (err) {
			toast({ status: 'error', title: err.message, isClosable: true });
		}
	};
	return { remove, isDeleting: deleteLead.isPending };
};

const Fact = ({ label, children }) => (
	<Box minW={0}>
		<Text
			fontSize='xs'
			color='gray.500'
			textTransform='uppercase'
			letterSpacing='wide'
			fontWeight='semibold'>
			{label}
		</Text>
		<Box fontSize='sm' color='gray.800' mt={1}>
			{children}
		</Box>
	</Box>
);

export const LeadFacts = ({ lead, columns = { base: 1, sm: 2, lg: 4 } }) => {
	const primary = primaryOf(lead);
	const dueColor = isOverdue(lead)
		? 'red.500'
		: isDue(lead)
			? 'orange.500'
			: 'gray.800';
	return (
		<>
			<SimpleGrid columns={columns} spacing={4}>
				<Fact label='Status'>
					<Text
						fontWeight='semibold'
						color={`${LEAD_STATUS_META[lead.status]?.color}.600`}>
						{LEAD_STATUS_META[lead.status]?.label ?? lead.status}
					</Text>
				</Fact>
				<Fact label='Email goes to'>
					<Text noOfLines={1} title={primary?.email}>
						{primary ? primary.name || primary.email : '–'}
					</Text>
					{primary?.email && primary.name && (
						<Text fontSize='xs' color='gray.500' fontFamily='mono' noOfLines={1}>
							{primary.email}
						</Text>
					)}
				</Fact>
				<Fact label='Next step'>
					{lead.nextAction ? (
						<>
							<Text noOfLines={2}>{lead.nextAction}</Text>
							{lead.nextActionAt && (
								<Text fontSize='xs' fontWeight='semibold' color={dueColor}>
									{isOverdue(lead) ? 'Overdue · ' : ''}
									{fmtDate(lead.nextActionAt)}
								</Text>
							)}
						</>
					) : (
						'–'
					)}
				</Fact>
				<Fact label='Last contact'>
					{lead.lastContactAt ? timeAgo(lead.lastContactAt) : 'Not yet'}
				</Fact>
			</SimpleGrid>
			{lead.website && (
				<HStack mt={4}>
					<Link
						href={
							/^https?:\/\//i.test(lead.website)
								? lead.website
								: `https://${lead.website}`
						}
						isExternal
						fontSize='sm'
						color='teal.600'>
						{lead.website}{' '}
						<FaExternalLinkAlt
							style={{ display: 'inline', marginLeft: 4 }}
							size={10}
						/>
					</Link>
				</HStack>
			)}
		</>
	);
};

/** Tab names as they appear in the `?tab=` URL parameter, in display order. */
export const LEAD_TABS = ['email', 'contacts', 'timeline', 'details'];

export const LeadTabs = ({
	lead,
	companyId,
	canUpdate,
	canDelete,
	onDelete,
	isDeleting,
	tab,
	onTabChange,
}) => (
	<Tabs
		colorScheme='teal'
		size='sm'
		isLazy
		{...(onTabChange
			? {
					index: Math.max(LEAD_TABS.indexOf(tab), 0),
					onChange: (i) => onTabChange(LEAD_TABS[i]),
				}
			: {})}>
		<TabList>
			<Tab>Email</Tab>
			<Tab>Contacts ({lead.contacts.length})</Tab>
			<Tab>Timeline ({lead.events.length})</Tab>
			<Tab>Details</Tab>
		</TabList>
		<TabPanels>
			<TabPanel px={0}>
				<EmailTab lead={lead} companyId={companyId} canUpdate={canUpdate} />
			</TabPanel>
			<TabPanel px={0}>
				<ContactsTab lead={lead} companyId={companyId} canUpdate={canUpdate} />
			</TabPanel>
			<TabPanel px={0}>
				<TimelineTab lead={lead} companyId={companyId} canUpdate={canUpdate} />
			</TabPanel>
			<TabPanel px={0}>
				<DetailsTab
					lead={lead}
					companyId={companyId}
					canUpdate={canUpdate}
					canDelete={canDelete}
					onDelete={onDelete}
					isDeleting={isDeleting}
				/>
			</TabPanel>
		</TabPanels>
	</Tabs>
);
